import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useUpdate } from '../contexts/UpdateContext';
import { SIDEBAR_SECTIONS } from '../config/sidebarConfig';

const isElectron = typeof window !== 'undefined' && !!window.appUpdater;

const EXPANDED_W = 220;
const COLLAPSED_W = 56;

/* SVG icon registry — add entries here as new sidebar items need icons */
export const SIDEBAR_ICONS = {
  'home': (cls = 'w-5 h-5') => (
    <svg className={cls} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.75}>
      <path strokeLinecap="round" strokeLinejoin="round"
        d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"
      />
    </svg>
  ),
  'chart-bar': (cls = 'w-5 h-5') => (
    <svg className={cls} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.75}>
      <path strokeLinecap="round" strokeLinejoin="round"
        d="M9 19v-6m4 6V5m4 14v-9M5 21h14a0 0 0 000 0M5 21V3"
      />
    </svg>
  ),
  'lightbulb': (cls = 'w-5 h-5') => (
    <svg className={cls} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.75}>
      <path strokeLinecap="round" strokeLinejoin="round"
        d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"
      />
    </svg>
  ),
  'map': (cls = 'w-5 h-5') => (
    <svg className={cls} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.75}>
      <path strokeLinecap="round" strokeLinejoin="round"
        d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7"
      />
    </svg>
  ),
  'document-text': (cls = 'w-5 h-5') => (
    <svg className={cls} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.75}>
      <path strokeLinecap="round" strokeLinejoin="round"
        d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
      />
    </svg>
  ),
  'envelope': (cls = 'w-5 h-5') => (
    <svg className={cls} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.75}>
      <path strokeLinecap="round" strokeLinejoin="round"
        d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
      />
    </svg>
  ),
  'chat-bubble': (cls = 'w-5 h-5') => (
    <svg className={cls} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.75}>
      <path strokeLinecap="round" strokeLinejoin="round"
        d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
      />
    </svg>
  ),
  'microphone': (cls = 'w-5 h-5') => (
    <svg className={cls} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.75}>
      <path strokeLinecap="round" strokeLinejoin="round"
        d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z"
      />
    </svg>
  ),
  'academic-cap': (cls = 'w-5 h-5') => (
    <svg className={cls} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.75}>
      <path strokeLinecap="round" strokeLinejoin="round"
        d="M12 14l9-5-9-5-9 5 9 5zm0 0l6.16-3.422a12.083 12.083 0 01.665 6.479A11.952 11.952 0 0012 20.055a11.952 11.952 0 00-6.824-2.998 12.078 12.078 0 01.665-6.479L12 14zm-4 6v-7.5l4-2.222"
      />
    </svg>
  ),
  // AI Course Builder: building something new with AI.
  'sparkles': (cls = 'w-5 h-5') => (
    <svg className={cls} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.75}>
      <path strokeLinecap="round" strokeLinejoin="round"
        d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z"
      />
    </svg>
  ),
  // Certification exam prep: a certificate's seal.
  'badge-check': (cls = 'w-5 h-5') => (
    <svg className={cls} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.75}>
      <path strokeLinecap="round" strokeLinejoin="round"
        d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z"
      />
    </svg>
  ),
  'brain': (cls = 'w-5 h-5') => (
    <svg className={cls} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.75}>
      <path strokeLinecap="round" strokeLinejoin="round"
        d="M5.121 17.804A13.937 13.937 0 0112 16c2.5 0 4.847.655 6.879 1.804M15 10a3 3 0 11-6 0 3 3 0 016 0zm6 2a9 9 0 11-18 0 9 9 0 0118 0z"
      />
    </svg>
  ),
  'user-group': (cls = 'w-5 h-5') => (
    <svg className={cls} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.75}>
      <path strokeLinecap="round" strokeLinejoin="round"
        d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
      />
    </svg>
  ),
  'trophy': (cls = 'w-5 h-5') => (
    <svg className={cls} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.75}>
      <path strokeLinecap="round" strokeLinejoin="round"
        d="M8 21h8m-4-4v4m-5-17h10v4a5 5 0 01-10 0V4zM7 6H4v1a3 3 0 003 3m10-4h3v1a3 3 0 01-3 3"
      />
    </svg>
  ),
  'building': (cls = 'w-5 h-5') => (
    <svg className={cls} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.75}>
      <path strokeLinecap="round" strokeLinejoin="round"
        d="M3 21h18M5 21V5a2 2 0 012-2h6a2 2 0 012 2v16M9 7h2m-2 4h2m-2 4h2m4-8h2a2 2 0 012 2v10"
      />
    </svg>
  ),
};

/* chevron-double-left / right for the collapse toggle */
function CollapseIcon({ collapsed }) {
  return (
    <svg className="w-4 h-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      {collapsed
        ? <path strokeLinecap="round" strokeLinejoin="round" d="M13 5l7 7-7 7M5 5l7 7-7 7" />
        : <path strokeLinecap="round" strokeLinejoin="round" d="M11 19l-7-7 7-7m8 14l-7-7 7-7" />}
    </svg>
  );
}

function UpdateButton({ collapsed }) {
  const ctx = useUpdate();
  if (!isElectron || !ctx) return null;

  // appVersion is the INSTALLED version and comes from the context, which
  // fetches it once; ctx.version is the version being offered and is empty
  // until an update exists.
  const { state, version, percent, setOpen, appVersion } = ctx;

  // Always rendered on desktop. Idle is the common case, so it stays muted and
  // just reports the installed version; downloading and ready are the two states
  // that earn colour.
  const isReady       = state === 'downloaded';
  const isDownloading = state === 'downloading';
  const isChecking    = state === 'checking';

  const tooltipText = isReady
    ? `v${version} ready — click to restart & install`
    : isDownloading
      ? `Downloading update v${version}… ${percent}%`
      : isChecking
        ? 'Checking for updates…'
        : `LearnXops${appVersion ? ` v${appVersion}` : ''} — click to check for updates`;

  const handleClick = () => {
    if (isReady) { window.appUpdater.install(); return; }
    if (isDownloading) { setOpen(true); return; }
    // Idle: run a manual check. Main flags it as manual, so the 'checking'
    // event opens the modal on its own — which then shows the result.
    window.appUpdater.check?.();
    setOpen(true);
  };

  const tone = isReady
    ? 'text-emerald-300 bg-emerald-500/10 ring-1 ring-emerald-500/30 hover:bg-emerald-500/20'
    : isDownloading
      ? 'text-indigo-300 bg-indigo-500/10 ring-1 ring-indigo-500/30'
      : 'text-gray-400 hover:text-gray-200 hover:bg-white/5';

  const primary = isReady
    ? 'Restart & Install'
    : isDownloading
      ? `Downloading… ${percent}%`
      : isChecking
        ? 'Checking…'
        : `v${appVersion || '—'}`;

  // Secondary line: the offered version while updating, otherwise the hint.
  const secondary = isReady || isDownloading
    ? (version ? `v${version}` : '')
    : isChecking ? '' : 'Check for updates';

  return (
    <div className="flex-shrink-0 px-2 pb-1" title={tooltipText}>
      <button
        onClick={handleClick}
        aria-label={tooltipText}
        className={[
          'relative flex items-center rounded-xl transition-all w-full',
          collapsed ? 'justify-center w-9 h-9 mx-auto' : 'gap-2.5 px-3 py-2',
          tone,
        ].join(' ')}
      >
        {isDownloading || isChecking ? (
          <div className="w-4 h-4 flex-shrink-0 border-2 border-current border-t-transparent rounded-full animate-spin opacity-70" />
        ) : isReady ? (
          <svg className="w-4 h-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
          </svg>
        ) : (
          /* idle — refresh arrows, reads as "check", not "download pending" */
          <svg className="w-4 h-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
        )}

        {!collapsed && (
          <div className="min-w-0 flex-1 text-left">
            <div className="text-xs font-semibold leading-tight whitespace-nowrap truncate">
              {primary}
            </div>
            {secondary && (
              <div className="text-[10px] opacity-70 mt-0.5 whitespace-nowrap truncate">
                {secondary}
              </div>
            )}
            {isDownloading && (
              <div className="mt-1.5 h-1 rounded-full overflow-hidden" style={{ background: 'rgba(99,102,241,0.2)' }}>
                <div
                  className="h-full rounded-full bg-indigo-400 transition-all duration-300"
                  style={{ width: `${percent}%` }}
                />
              </div>
            )}
          </div>
        )}

        {/* Status dot when collapsed — only for states that need attention. */}
        {collapsed && (isReady || isDownloading) && (
          <span className={`absolute -top-0.5 -right-0.5 w-2.5 h-2.5 rounded-full ${isReady ? 'bg-emerald-400' : 'bg-indigo-400 animate-pulse'}`} />
        )}
      </button>
    </div>
  );
}

export default function LeftSidebar() {
  const location = useLocation();
  const { isPaid } = useAuth();

  /* expanded by default; persisted in localStorage */
  const [collapsed, setCollapsed] = useState(
    () => localStorage.getItem('lxops_sidebar_collapsed') !== 'false'
  );

  const toggle = () =>
    setCollapsed((v) => {
      const next = !v;
      localStorage.setItem('lxops_sidebar_collapsed', String(next));
      return next;
    });

  const w = collapsed ? COLLAPSED_W : EXPANDED_W;
  const transition = 'width 200ms ease';

  return (
    <aside
      data-tour="sidebar"
      className="hidden sm:block flex-shrink-0"
      style={{ width: w, transition }}
    >
      <div
        className="sticky flex flex-col overflow-hidden"
        style={{
          top: '3.5rem',
          height: 'calc(100vh - 3.5rem)',
          width: w,
          transition,
          borderRight: '1px solid var(--lx-border)',
        }}
      >
        {/* ── scrollable item area ── */}
        <div className="flex-1 overflow-y-auto overflow-x-hidden py-3 px-2 space-y-1">
          {SIDEBAR_SECTIONS.map((section, si) => (
            <div key={section.id}>
              {si > 0 && (
                <div className="my-2 mx-2 h-px" style={{ background: 'var(--lx-border)' }} />
              )}

              {/* section label — only when expanded */}
              {!collapsed && (
                <p className="px-3 pb-1 pt-0.5 text-[10px] font-semibold uppercase tracking-wider text-gray-600 whitespace-nowrap">
                  {section.label}
                </p>
              )}

              {section.items.filter((item) => !item.visible || item.visible()).map((item) => {
                const active   = item.isActive(location);
                const locked   = item.paidOnly && !isPaid;
                const dest     = item.to; // page handles its own upgrade gate
                const renderIcon = SIDEBAR_ICONS[item.icon];

                return (
                  <Link
                    key={item.id}
                    to={dest}
                    title={collapsed ? (locked ? `${item.label} — Pro members only` : item.label) : undefined}
                    className={[
                      'relative flex items-center rounded-xl transition-all select-none',
                      collapsed ? 'justify-center w-9 h-9 mx-auto' : 'gap-3 px-3 py-2.5',
                      active
                        ? 'text-white bg-indigo-600/20 ring-1 ring-indigo-500/40'
                        : locked
                        ? 'text-gray-600 hover:text-gray-400 hover:bg-white/[0.04]'
                        : 'text-gray-400 hover:text-white hover:bg-white/[0.07]',
                    ].join(' ')}
                  >
                    {/* icon */}
                    {renderIcon && renderIcon('w-5 h-5 flex-shrink-0')}

                    {/* label + sub — expanded only */}
                    {!collapsed && (
                      <div className="min-w-0 flex-1 overflow-hidden">
                        <div className="text-sm font-medium leading-tight whitespace-nowrap truncate">
                          {item.label}
                        </div>
                        {item.sub && (
                          <div className="text-[10px] text-gray-600 mt-0.5 whitespace-nowrap truncate">
                            {item.sub}
                          </div>
                        )}
                      </div>
                    )}

                    {/* PRO badge — expanded only */}
                    {!collapsed && item.paidOnly && (
                      <span className={[
                        'flex-shrink-0 text-[9px] font-bold px-1.5 py-0.5 rounded-full',
                        locked
                          ? 'bg-gray-700/60 text-gray-500'
                          : 'bg-indigo-500/20 text-indigo-400',
                      ].join(' ')}>
                        {locked ? '🔒 PRO' : 'PRO'}
                      </span>
                    )}

                    {/* lock pip — collapsed only */}
                    {collapsed && locked && (
                      <span className="absolute -top-0.5 -right-0.5 w-3.5 h-3.5 rounded-full bg-gray-800 border border-gray-600 flex items-center justify-center text-[7px]">
                        🔒
                      </span>
                    )}

                    {/* active left accent — expanded only */}
                    {active && !collapsed && (
                      <span className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-5 rounded-r-full bg-indigo-400" />
                    )}

                    {/* active dot — collapsed only */}
                    {active && collapsed && (
                      <span className="absolute right-0.5 top-0.5 w-1.5 h-1.5 rounded-full bg-indigo-400" />
                    )}
                  </Link>
                );
              })}
            </div>
          ))}
        </div>

        {/* ── desktop app download (web only) ── */}
        {!isElectron && (
          <div className="flex-shrink-0 px-2 pb-1">
            <Link
              to="/download"
              title="Download LearnXops Desktop"
              className={[
                'flex items-center rounded-xl transition-all text-indigo-400 hover:text-indigo-300 hover:bg-indigo-500/10',
                collapsed ? 'justify-center w-9 h-9 mx-auto' : 'gap-2.5 px-3 py-2',
              ].join(' ')}
            >
              <svg className="w-4 h-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.75}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"/>
              </svg>
              {!collapsed && (
                <div className="min-w-0">
                  <div className="text-xs font-semibold leading-tight whitespace-nowrap">Get Desktop App</div>
                  <div className="text-[10px] text-indigo-500 mt-0.5 whitespace-nowrap">Mac · Windows · Linux</div>
                </div>
              )}
            </Link>
          </div>
        )}

        {/* ── updater: always shown on desktop; idle reports the installed version ── */}
        <UpdateButton collapsed={collapsed} />

        {/* ── collapse / expand toggle ── */}
        <div className="flex-shrink-0 px-2 py-2" style={{ borderTop: '1px solid var(--lx-border)' }}>
          <button
            onClick={toggle}
            title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            className={[
              'flex items-center rounded-xl text-gray-600 hover:text-gray-300 hover:bg-white/[0.06] transition-all',
              collapsed ? 'justify-center w-9 h-9 mx-auto' : 'gap-2 w-full px-3 py-2',
            ].join(' ')}
          >
            <CollapseIcon collapsed={collapsed} />
            {!collapsed && <span className="text-xs whitespace-nowrap">Collapse</span>}
          </button>
        </div>
      </div>
    </aside>
  );
}
